import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Package, Plus, Trash2, Edit, Search, ClipboardList, AlertTriangle, CheckCircle2, XCircle, Wrench,
  DollarSign, ChevronDown, ChevronRight, Building2,
} from "lucide-react";
import type { Apartment } from "@shared/schema";
import { INVENTORY_ZONES, INVENTORY_CONDITIONS } from "@shared/schema";

const zoneLabels: Record<string, string> = {
  kitchen: "Kitchen", bathroom: "Bathroom", bedroom: "Bedroom",
  living_room: "Living Room", dining_room: "Dining Room", balcony: "Balcony",
  laundry: "Laundry", hallway: "Hallway", other: "Other",
};

const conditionConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof CheckCircle2 }> = {
  good: { label: "Good", variant: "default", icon: CheckCircle2 },
  damaged: { label: "Damaged", variant: "destructive", icon: AlertTriangle },
  needs_replacement: { label: "Needs Replacement", variant: "destructive", icon: Wrench },
  missing: { label: "Missing", variant: "outline", icon: XCircle },
};

interface InventoryTemplate {
  id: number;
  organizationId: number;
  name: string;
  zone: string;
  defaultQuantity: number;
  unitCost: string | null;
  createdAt: string;
}

interface ApartmentInventoryItem {
  id: number;
  apartmentId: number;
  templateId: number;
  quantity: number;
  condition: string;
  zone: string;
  notes: string | null;
  cost: string | null;
  updatedAt: string;
  createdAt: string;
}

interface TemplateFormData {
  name: string;
  zone: string;
  defaultQuantity: number;
  unitCost: string | null;
}

interface InventoryItemUpdate {
  quantity: number;
  condition: string;
  zone: string;
  notes: string | null;
}

interface InventoryReport {
  id: number;
  apartmentId: number;
  items: { templateName: string; condition: string; notes?: string }[];
  createdAt: string;
}

function formatCurrency(val: string | null | undefined): string {
  if (val === null || val === undefined) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}

export default function AdminInventory() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InventoryTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: "", zone: "other", defaultQuantity: "1", unitCost: "" });
  const [filterApartment, setFilterApartment] = useState("all");
  const [filterZone, setFilterZone] = useState("all");
  const [filterCondition, setFilterCondition] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTemplateId, setAssignTemplateId] = useState<number | null>(null);
  const [selectedApartments, setSelectedApartments] = useState<number[]>([]);
  const [editingItem, setEditingItem] = useState<ApartmentInventoryItem | null>(null);
  const [editForm, setEditForm] = useState({ quantity: "1", condition: "good", zone: "other", notes: "" });
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<ApartmentInventoryItem | null>(null);
  const [deleteConfirmTemplate, setDeleteConfirmTemplate] = useState<InventoryTemplate | null>(null);
  const [expandedApartments, setExpandedApartments] = useState<Set<number>>(new Set());

  const { data: templates = [] } = useQuery<InventoryTemplate[]>({
    queryKey: ["/api/admin/inventory-templates"],
  });

  const { data: allInventory = [] } = useQuery<ApartmentInventoryItem[]>({
    queryKey: ["/api/admin/apartment-inventory"],
  });

  const { data: apartments = [] } = useQuery<Apartment[]>({
    queryKey: ["/api/apartments"],
  });

  const { data: reports = [] } = useQuery<InventoryReport[]>({
    queryKey: ["/api/admin/inventory-reports"],
  });

  const templatesMap = useMemo(() => {
    const m = new Map<number, InventoryTemplate>();
    templates.forEach(t => m.set(t.id, t));
    return m;
  }, [templates]);

  const apartmentsMap = useMemo(() => {
    const m = new Map<number, Apartment>();
    apartments.forEach(a => m.set(a.id, a));
    return m;
  }, [apartments]);

  const createTemplate = useMutation({
    mutationFn: (data: TemplateFormData) => apiRequest("POST", "/api/admin/inventory-templates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory-templates"] });
      setTemplateDialogOpen(false);
      setTemplateForm({ name: "", zone: "other", defaultQuantity: "1", unitCost: "" });
      toast({ title: "Template created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateTemplate = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TemplateFormData }) => apiRequest("PUT", `/api/admin/inventory-templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory-templates"] });
      setEditingTemplate(null);
      setTemplateDialogOpen(false);
      setTemplateForm({ name: "", zone: "other", defaultQuantity: "1", unitCost: "" });
      toast({ title: "Template updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/inventory-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apartment-inventory"] });
      toast({ title: "Template deleted" });
    },
  });

  const bulkAssign = useMutation({
    mutationFn: ({ templateId, apartmentIds }: { templateId: number; apartmentIds: number[] }) =>
      apiRequest("POST", `/api/admin/inventory-templates/${templateId}/assign`, { apartmentIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apartment-inventory"] });
      setAssignDialogOpen(false);
      setSelectedApartments([]);
      setAssignTemplateId(null);
      toast({ title: "Items assigned to apartments" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateItem = useMutation({
    mutationFn: ({ aptId, itemId, data }: { aptId: number; itemId: number; data: InventoryItemUpdate }) =>
      apiRequest("PUT", `/api/admin/apartments/${aptId}/inventory/${itemId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apartment-inventory"] });
      setEditingItem(null);
      toast({ title: "Item updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteItem = useMutation({
    mutationFn: ({ aptId, itemId }: { aptId: number; itemId: number }) =>
      apiRequest("DELETE", `/api/admin/apartments/${aptId}/inventory/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apartment-inventory"] });
      toast({ title: "Item removed" });
    },
  });

  const handleTemplateSubmit = () => {
    const data: TemplateFormData = {
      name: templateForm.name,
      zone: templateForm.zone,
      defaultQuantity: parseInt(templateForm.defaultQuantity) || 1,
      unitCost: templateForm.unitCost ? templateForm.unitCost : null,
    };
    if (editingTemplate) {
      updateTemplate.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplate.mutate(data);
    }
  };

  const filteredInventory = allInventory.filter(item => {
    if (filterApartment !== "all" && item.apartmentId.toString() !== filterApartment) return false;
    if (filterZone !== "all" && item.zone !== filterZone) return false;
    if (filterCondition !== "all" && item.condition !== filterCondition) return false;
    if (searchQuery) {
      const template = templatesMap.get(item.templateId);
      const apt = apartmentsMap.get(item.apartmentId);
      const q = searchQuery.toLowerCase();
      return (template?.name || "").toLowerCase().includes(q) || (apt?.name || "").toLowerCase().includes(q);
    }
    return true;
  });

  const conditionCounts = useMemo(() => {
    const counts: Record<string, number> = { good: 0, damaged: 0, needs_replacement: 0, missing: 0 };
    allInventory.forEach(i => { counts[i.condition] = (counts[i.condition] || 0) + 1; });
    return counts;
  }, [allInventory]);

  const totalValue = useMemo(() => {
    let total = 0;
    allInventory.forEach(item => {
      if (item.cost !== null && item.cost !== undefined) {
        const c = parseFloat(item.cost);
        if (!isNaN(c)) total += c * item.quantity;
      }
    });
    return total;
  }, [allInventory]);

  const inventoryByApartment = useMemo(() => {
    const grouped = new Map<number, ApartmentInventoryItem[]>();
    allInventory.forEach(item => {
      const list = grouped.get(item.apartmentId) || [];
      list.push(item);
      grouped.set(item.apartmentId, list);
    });
    return grouped;
  }, [allInventory]);

  const toggleApartmentExpand = (aptId: number) => {
    setExpandedApartments(prev => {
      const next = new Set(prev);
      if (next.has(aptId)) next.delete(aptId);
      else next.add(aptId);
      return next;
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="inventory-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold flex items-center gap-2" data-testid="inventory-title">
              <Package className="h-6 w-6 text-primary" />
              Inventory
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Track physical items across all apartments</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Good</p>
                <p className="text-xl font-bold" data-testid="stat-good">{conditionCounts.good}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Damaged</p>
                <p className="text-xl font-bold" data-testid="stat-damaged">{conditionCounts.damaged}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Replace</p>
                <p className="text-xl font-bold" data-testid="stat-replace">{conditionCounts.needs_replacement}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Missing</p>
                <p className="text-xl font-bold" data-testid="stat-missing">{conditionCounts.missing}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Value</p>
                <p className="text-xl font-bold" data-testid="stat-total-value">${totalValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent></Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="overview" className="gap-1.5" data-testid="tab-overview">
              <ClipboardList className="h-4 w-4" />
              All Items
            </TabsTrigger>
            <TabsTrigger value="by-apartment" className="gap-1.5" data-testid="tab-by-apartment">
              <Building2 className="h-4 w-4" />
              By Apartment
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-1.5" data-testid="tab-templates">
              <Package className="h-4 w-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-1.5" data-testid="tab-reports">
              <ClipboardList className="h-4 w-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-inventory"
                />
              </div>
              <Select value={filterApartment} onValueChange={setFilterApartment}>
                <SelectTrigger className="w-[180px]" data-testid="select-filter-apartment">
                  <SelectValue placeholder="All Apartments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apartments</SelectItem>
                  {apartments.map(a => (
                    <SelectItem key={a.id} value={a.id.toString()}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterZone} onValueChange={setFilterZone}>
                <SelectTrigger className="w-[150px]" data-testid="select-filter-zone">
                  <SelectValue placeholder="All Zones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Zones</SelectItem>
                  {INVENTORY_ZONES.map(z => (
                    <SelectItem key={z} value={z}>{zoneLabels[z] || z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCondition} onValueChange={setFilterCondition}>
                <SelectTrigger className="w-[160px]" data-testid="select-filter-condition">
                  <SelectValue placeholder="All Conditions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  {INVENTORY_CONDITIONS.map(c => (
                    <SelectItem key={c} value={c}>{conditionConfig[c]?.label || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filteredInventory.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Package className="h-16 w-16 text-muted-foreground opacity-30 mb-4" />
                  <h3 className="font-semibold text-lg">No inventory items</h3>
                  <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                    Create templates first, then assign them to apartments.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredInventory.map(item => {
                  const template = templatesMap.get(item.templateId);
                  const apt = apartmentsMap.get(item.apartmentId);
                  const cConfig = conditionConfig[item.condition] || conditionConfig.good;
                  const CondIcon = cConfig.icon;
                  const itemTotal = item.cost !== null && item.cost !== undefined ? parseFloat(item.cost) * item.quantity : null;
                  return (
                    <Card key={item.id} data-testid={`inventory-item-${item.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium text-sm">{template?.name || "Unknown"}</span>
                              <Badge variant="outline" className="text-[10px]">{zoneLabels[item.zone] || item.zone}</Badge>
                              <Badge variant={cConfig.variant} className="text-[10px]">
                                <CondIcon className="w-3 h-3 mr-1" />
                                {cConfig.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{apt?.name || `Apt #${item.apartmentId}`}</span>
                              <span>Qty: {item.quantity}</span>
                              {item.cost !== null && item.cost !== undefined && <span data-testid={`item-cost-${item.id}`}>{formatCurrency(item.cost)}/ea</span>}
                              {itemTotal !== null && !isNaN(itemTotal) && <span data-testid={`item-total-${item.id}`}>Total: ${itemTotal.toFixed(2)}</span>}
                              {item.notes && <span>{item.notes}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingItem(item);
                              setEditForm({
                                quantity: String(item.quantity),
                                condition: item.condition,
                                zone: item.zone,
                                notes: item.notes || "",
                              });
                            }} data-testid={`button-edit-item-${item.id}`}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmItem(item)} data-testid={`button-delete-item-${item.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="by-apartment" className="space-y-4 mt-4">
            {inventoryByApartment.size === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Building2 className="h-16 w-16 text-muted-foreground opacity-30 mb-4" />
                  <h3 className="font-semibold text-lg">No inventory assigned</h3>
                  <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                    Assign templates to apartments to see inventory grouped here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {Array.from(inventoryByApartment.entries()).map(([aptId, items]) => {
                  const apt = apartmentsMap.get(aptId);
                  const isExpanded = expandedApartments.has(aptId);
                  const aptTotal = items.reduce((sum, item) => {
                    if (item.cost !== null && item.cost !== undefined) {
                      const c = parseFloat(item.cost);
                      if (!isNaN(c)) return sum + c * item.quantity;
                    }
                    return sum;
                  }, 0);
                  const condSummary: Record<string, number> = {};
                  items.forEach(item => {
                    condSummary[item.condition] = (condSummary[item.condition] || 0) + 1;
                  });

                  return (
                    <Card key={aptId} data-testid={`apt-group-${aptId}`}>
                      <CardContent className="p-0">
                        <button
                          className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-muted/50 transition-colors"
                          onClick={() => toggleApartmentExpand(aptId)}
                          data-testid={`button-expand-apt-${aptId}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                            <div>
                              <span className="font-medium text-sm">{apt?.name || `Apartment #${aptId}`}</span>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <Badge variant="secondary" className="text-[10px]">{items.length} items</Badge>
                                {aptTotal > 0 && <Badge variant="outline" className="text-[10px]" data-testid={`apt-value-${aptId}`}>${aptTotal.toFixed(2)}</Badge>}
                                {Object.entries(condSummary).map(([cond, count]) => {
                                  const cc = conditionConfig[cond];
                                  if (!cc || cond === "good") return null;
                                  return (
                                    <Badge key={cond} variant={cc.variant} className="text-[10px]">
                                      {count} {cc.label.toLowerCase()}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="border-t px-4 pb-4 pt-2 space-y-1">
                            {items.map(item => {
                              const template = templatesMap.get(item.templateId);
                              const cConfig = conditionConfig[item.condition] || conditionConfig.good;
                              const CondIcon = cConfig.icon;
                              return (
                                <div key={item.id} className="group flex items-center justify-between gap-3 py-2 px-2 rounded hover:bg-muted/50" data-testid={`apt-inv-item-${item.id}`}>
                                  <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                    <span className="text-sm font-medium">{template?.name || "Unknown"}</span>
                                    <Badge variant="outline" className="text-[10px]">x{item.quantity}</Badge>
                                    <Badge variant="outline" className="text-[10px]">{zoneLabels[item.zone] || item.zone}</Badge>
                                    <Badge variant={cConfig.variant} className="text-[10px]">
                                      <CondIcon className="w-3 h-3 mr-1" />
                                      {cConfig.label}
                                    </Badge>
                                    {item.cost !== null && item.cost !== undefined && <span className="text-xs text-muted-foreground">{formatCurrency(item.cost)}/ea</span>}
                                    {item.notes && <span className="text-xs text-muted-foreground truncate">{item.notes}</span>}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0 invisible group-hover:visible">
                                    <Button variant="ghost" size="sm" onClick={() => {
                                      setEditingItem(item);
                                      setEditForm({
                                        quantity: String(item.quantity),
                                        condition: item.condition,
                                        zone: item.zone,
                                        notes: item.notes || "",
                                      });
                                    }} data-testid={`button-edit-aptitem-${item.id}`}>
                                      <Edit className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmItem(item)} data-testid={`button-delete-aptitem-${item.id}`}>
                                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Item Templates</h2>
              <Button onClick={() => { setTemplateDialogOpen(true); setEditingTemplate(null); setTemplateForm({ name: "", zone: "other", defaultQuantity: "1", unitCost: "" }); }} data-testid="button-add-template">
                <Plus className="h-4 w-4 mr-2" /> Add Template
              </Button>
            </div>

            {templates.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Package className="h-16 w-16 text-muted-foreground opacity-30 mb-4" />
                  <h3 className="font-semibold text-lg">No templates yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                    Define inventory item templates that can be assigned to apartments.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {templates.map(t => (
                  <Card key={t.id} data-testid={`template-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium text-sm">{t.name}</span>
                            <Badge variant="outline" className="text-[10px]">{zoneLabels[t.zone] || t.zone}</Badge>
                            {t.unitCost !== null && t.unitCost !== undefined && <Badge variant="secondary" className="text-[10px]" data-testid={`template-cost-${t.id}`}>{formatCurrency(t.unitCost)}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">Default qty: {t.defaultQuantity}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => {
                            setAssignTemplateId(t.id);
                            setSelectedApartments([]);
                            setAssignDialogOpen(true);
                          }} data-testid={`button-assign-${t.id}`}>
                            Assign
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingTemplate(t);
                            setTemplateForm({ name: t.name, zone: t.zone, defaultQuantity: String(t.defaultQuantity), unitCost: t.unitCost || "" });
                            setTemplateDialogOpen(true);
                          }} data-testid={`button-edit-template-${t.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmTemplate(t)} data-testid={`button-delete-template-${t.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reports" className="space-y-4 mt-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Inventory Reports from Cleaning Jobs</h2>
            {reports.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <ClipboardList className="h-16 w-16 text-muted-foreground opacity-30 mb-4" />
                  <h3 className="font-semibold text-lg">No reports yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                    Reports are created when cleaners submit inventory checks during cleaning jobs.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => {
                  const apt = apartmentsMap.get(r.apartmentId);
                  const items = r.items || [];
                  const flaggedItems = items.filter((i) => i.condition !== "good");
                  return (
                    <Card key={r.id} data-testid={`report-${r.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{apt?.name || `Apt #${r.apartmentId}`}</span>
                          <span className="text-xs text-muted-foreground">
                            {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-[10px]">{items.length} items checked</Badge>
                          {flaggedItems.length > 0 && (
                            <Badge variant="destructive" className="text-[10px]">{flaggedItems.length} flagged</Badge>
                          )}
                        </div>
                        {flaggedItems.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {flaggedItems.map((fi, idx) => (
                              <div key={idx} className="text-xs flex items-center gap-2">
                                <span className="font-medium">{fi.templateName}</span>
                                <Badge variant={conditionConfig[fi.condition]?.variant || "secondary"} className="text-[10px]">
                                  {conditionConfig[fi.condition]?.label || fi.condition}
                                </Badge>
                                {fi.notes && <span className="text-muted-foreground">{fi.notes}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? "Edit Template" : "Add Template"}</DialogTitle>
              <DialogDescription>Define an inventory item that can be assigned to apartments.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={templateForm.name}
                  onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Queen Bed Sheet Set"
                  data-testid="input-template-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Default Zone</label>
                <Select value={templateForm.zone} onValueChange={v => setTemplateForm({ ...templateForm, zone: v })}>
                  <SelectTrigger data-testid="select-template-zone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_ZONES.map(z => (
                      <SelectItem key={z} value={z}>{zoneLabels[z] || z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Default Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={templateForm.defaultQuantity}
                  onChange={e => setTemplateForm({ ...templateForm, defaultQuantity: e.target.value })}
                  data-testid="input-template-quantity"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Unit Cost ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={templateForm.unitCost}
                  onChange={e => setTemplateForm({ ...templateForm, unitCost: e.target.value })}
                  placeholder="Optional"
                  data-testid="input-template-cost"
                />
              </div>
              <Button className="w-full" onClick={handleTemplateSubmit} disabled={!templateForm.name || createTemplate.isPending || updateTemplate.isPending} data-testid="button-save-template">
                {editingTemplate ? "Update Template" : "Create Template"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign to Apartments</DialogTitle>
              <DialogDescription>Select apartments to assign this item to.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {apartments.map(a => (
                <div key={a.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedApartments.includes(a.id)}
                    onCheckedChange={(checked) => {
                      setSelectedApartments(prev =>
                        checked ? [...prev, a.id] : prev.filter(id => id !== a.id)
                      );
                    }}
                    data-testid={`checkbox-apt-${a.id}`}
                  />
                  <span className="text-sm">{a.name}</span>
                </div>
              ))}
            </div>
            <Button
              className="w-full"
              onClick={() => assignTemplateId && bulkAssign.mutate({ templateId: assignTemplateId, apartmentIds: selectedApartments })}
              disabled={selectedApartments.length === 0 || bulkAssign.isPending}
              data-testid="button-confirm-assign"
            >
              Assign to {selectedApartments.length} apartment{selectedApartments.length !== 1 ? "s" : ""}
            </Button>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Inventory Item</DialogTitle>
              <DialogDescription>Update quantity, condition, zone, or notes.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Quantity</label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.quantity}
                  onChange={e => setEditForm({ ...editForm, quantity: e.target.value })}
                  data-testid="input-edit-item-quantity"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Condition</label>
                <Select value={editForm.condition} onValueChange={v => setEditForm({ ...editForm, condition: v })}>
                  <SelectTrigger data-testid="select-edit-item-condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_CONDITIONS.map(c => (
                      <SelectItem key={c} value={c}>{conditionConfig[c]?.label || c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Zone</label>
                <Select value={editForm.zone} onValueChange={v => setEditForm({ ...editForm, zone: v })}>
                  <SelectTrigger data-testid="select-edit-item-zone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_ZONES.map(z => (
                      <SelectItem key={z} value={z}>{zoneLabels[z] || z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Input
                  value={editForm.notes}
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Optional notes"
                  data-testid="input-edit-item-notes"
                />
              </div>
              <Button
                className="w-full"
                onClick={() => editingItem && updateItem.mutate({
                  aptId: editingItem.apartmentId,
                  itemId: editingItem.id,
                  data: { quantity: parseInt(editForm.quantity), condition: editForm.condition, zone: editForm.zone, notes: editForm.notes || null },
                })}
                disabled={updateItem.isPending}
                data-testid="button-save-item-edit"
              >
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DeleteConfirmDialog
        open={deleteConfirmItem !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmItem(null); }}
        title="Delete this inventory item?"
        description="This will permanently delete the inventory item from this apartment. Historical inventory reports referencing it will remain. This cannot be undone."
        confirmLabel="Delete Item"
        pending={deleteItem.isPending}
        onConfirm={() => { if (deleteConfirmItem) { deleteItem.mutate({ aptId: deleteConfirmItem.apartmentId, itemId: deleteConfirmItem.id }); setDeleteConfirmItem(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmTemplate !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmTemplate(null); }}
        title="Delete this inventory template?"
        description={deleteConfirmTemplate ? <>This will permanently delete the inventory template <strong>"{deleteConfirmTemplate.name}"</strong>. Inventory items already created from this template will remain in apartments. This cannot be undone.</> : null}
        confirmLabel="Delete Template"
        pending={deleteTemplate.isPending}
        onConfirm={() => { if (deleteConfirmTemplate) { deleteTemplate.mutate(deleteConfirmTemplate.id); setDeleteConfirmTemplate(null); } }}
      />
    </AdminLayout>
  );
}
